<template>
  <el-dialog
    v-model="visibleProxy"
    :title="$t('exportPreviewTitle')"
    modal-class="modal-class"
    width="1040px"
    align-center
    :destroy-on-close="true"
    :close-on-click-modal="false"
  >
    <div class="export-preview">
      <div class="export-preview__meta">
        <span class="export-preview__chip">
          <i class="bi bi-list-ul" aria-hidden="true"></i>
          {{ rows.length }} {{ $t("importRows") }}
        </span>
        <span v-if="fileName" class="export-preview__chip export-preview__chip--file" :title="fileName">
          <i class="bi bi-file-earmark-excel" aria-hidden="true"></i>
          {{ fileName }}
        </span>
      </div>

      <p class="export-preview__hint">{{ $t("exportPreviewText") }}</p>

      <div v-if="!rows.length" class="export-preview__empty">
        <div class="export-preview__empty-icon" aria-hidden="true">
          <i class="bi bi-file-earmark-excel"></i>
        </div>
        <h4 class="export-preview__empty-title">{{ $t("exportPreviewEmptyTitle") }}</h4>
        <p class="export-preview__empty-text">{{ $t("exportPreviewEmptyText") }}</p>
      </div>

      <div v-else class="export-preview__table-wrap">
        <el-table
          size="small"
          border
          stripe
          class="el-table-css export-preview__table"
          max-height="460"
          :data="rows"
        >
          <el-table-column label="#" width="48" fixed="left" align="center">
            <template #default="scope">
              <span class="export-preview__row-index">{{ scope.$index + 1 }}</span>
            </template>
          </el-table-column>
          <el-table-column
            v-for="column in columns"
            :key="column"
            :prop="column"
            :label="column"
            min-width="120"
            show-overflow-tooltip
          />
        </el-table>
      </div>

      <div class="export-preview__footer">
        <button type="button" class="action-btn action-btn--muted" @click="close">
          {{ $t("commonCancel") }}
        </button>
        <button
          type="button"
          class="action-btn action-btn--excel"
          :disabled="!rows.length || exporting"
          @click="confirmExport"
        >
          <i class="bi bi-file-earmark-arrow-down" aria-hidden="true"></i>
          <span>{{ exporting ? $t("exportPreviewLoading") : $t("exportConfirm") }}</span>
        </button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: "InsuredExportPreviewDialog",
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    rows: {
      type: Array,
      default: () => [],
    },
    fileName: {
      type: String,
      default: "",
    },
    exporting: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "export"],
  computed: {
    visibleProxy: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    columns() {
      if (!this.rows.length) {
        return [];
      }
      return Object.keys(this.rows[0]);
    },
  },
  methods: {
    close() {
      this.visibleProxy = false;
    },
    confirmExport() {
      if (!this.rows.length || this.exporting) {
        return;
      }
      this.$emit("export");
    },
  },
};
</script>

<style scoped>
.export-preview {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.export-preview__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.export-preview__chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  background: var(--app-surface-muted);
  color: var(--app-text);
  font-size: 0.78rem;
  font-weight: 600;
}

.export-preview__chip--file {
  max-width: 100%;
  background: color-mix(in srgb, #22c55e 10%, var(--app-surface));
  color: #15803d;
}

.export-preview__hint {
  margin: 0;
  font-size: 0.84rem;
  color: var(--app-text);
  opacity: 0.85;
}

.export-preview__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 220px;
  padding: 1.5rem 1rem;
  border: 1px dashed var(--app-border);
  border-radius: var(--app-radius-sm);
  background: var(--app-surface-muted);
  text-align: center;
}

.export-preview__empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  margin-bottom: 0.65rem;
  border-radius: 999px;
  background: color-mix(in srgb, #22c55e 12%, var(--app-surface));
  color: #16a34a;
  font-size: 1.15rem;
}

.export-preview__empty-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--app-heading);
}

.export-preview__empty-text {
  margin: 0.35rem 0 0;
  max-width: 24rem;
  font-size: 0.84rem;
  color: var(--app-text);
  line-height: 1.45;
}

.export-preview__table-wrap {
  border: 1px solid var(--app-border);
  border-radius: var(--app-radius-sm);
  overflow: hidden;
  background: var(--app-surface);
}

.export-preview__table :deep(.el-table__header th.el-table__cell) {
  font-size: 0.72rem;
}

.export-preview__table :deep(.el-table__cell) {
  padding-top: 0.35rem;
  padding-bottom: 0.35rem;
}

.export-preview__row-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.45rem;
  height: 1.45rem;
  border-radius: 6px;
  background: var(--app-surface-muted);
  color: var(--app-text);
  font-size: 0.72rem;
  font-weight: 700;
}

.export-preview__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding-top: 0.25rem;
}
</style>
